import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const [companies, setCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinVerified, setPinVerified] = useState({});

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`, { withCredentials: true });
      setCompanies(response.data);
      // Always replace currentCompany with the fresh server copy so edits propagate everywhere
      setCurrentCompany((prev) => {
        if (prev) {
          const fresh = response.data.find((c) => c.id === prev.id);
          if (fresh) return fresh;
        }
        const saved = localStorage.getItem('currentCompanyId');
        const found = response.data.find((c) => c.id === saved);
        return found || response.data[0] || null;
      });
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCompanies = fetchCompanies;

  const switchCompany = (company) => {
    setCurrentCompany(company);
    localStorage.setItem('currentCompanyId', company.id);
  };

  const verifyPin = async (companyId, pin) => {
    const res = await axios.post(`${API_URL}/api/companies/${companyId}/verify-pin`, { pin }, { withCredentials: true });
    if (res.data.verified) {
      setPinVerified(prev => ({ ...prev, [companyId]: true }));
      return true;
    }
    return false;
  };

  const isPinRequired = (company) => {
    if (!company?.is_personal) return false;
    return !pinVerified[company.id];
  };

  const value = {
    companies,
    currentCompany,
    switchCompany,
    fetchCompanies,
    refreshCompanies,
    loading,
    verifyPin,
    isPinRequired,
    pinVerified
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}
